import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Card, CardContent, CardHeader, CardTitle 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Collapsible, CollapsibleContent, CollapsibleTrigger 
} from '../components/ui/collapsible';
import { 
  ChevronDown, ChevronRight, Plus, Trash2, Lock, Unlock, 
  RefreshCw, Save, FileText, Building2, Shield, Leaf, 
  TrendingUp, Calculator, AlertTriangle, Clock, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section status badge component
const SectionStatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { label: 'Draft', variant: 'secondary' },
    edited: { label: 'Edited', variant: 'default' },
    approved: { label: 'Approved', variant: 'success' },
    locked: { label: 'Locked', variant: 'destructive' }
  };
  
  const config = statusConfig[status] || statusConfig.draft;
  
  return (
    <Badge variant={config.variant} className="ml-2 text-xs">
      {config.label}
    </Badge>
  );
};

// Collapsible section wrapper
const CollapsibleSection = ({ 
  title, 
  icon: Icon, 
  status, 
  isOpen, 
  onToggle, 
  children,
  isLocked = false
}) => {
  return (
    <Collapsible open={isOpen} onOpenChange={onToggle} className="mb-4">
      <CollapsibleTrigger asChild>
        <div 
          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
          data-testid={`section-trigger-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {Icon && <Icon className="h-5 w-5 text-blue-600" />}
            <span className="font-semibold text-gray-800">{title}</span>
            <SectionStatusBadge status={status} />
          </div>
          {isLocked && <Lock className="h-4 w-4 text-red-500" />}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

// Lease Entry Row Component
const LeaseEntryRow = ({ entry, index, onUpdate, onDelete, disabled }) => {
  return (
    <div className="grid grid-cols-12 gap-2 p-2 bg-white border rounded mb-2" data-testid={`lease-entry-${index}`}>
      <div className="col-span-2">
        <Input
          placeholder="Floor"
          value={entry.floor_description || ''}
          onChange={(e) => onUpdate(index, 'floor_description', e.target.value)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-2">
        <Input
          placeholder="Tenant"
          value={entry.tenant_name || ''}
          onChange={(e) => onUpdate(index, 'tenant_name', e.target.value)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-1">
        <Input
          type="number"
          placeholder="Term"
          value={entry.lease_term_years || ''}
          onChange={(e) => onUpdate(index, 'lease_term_years', parseInt(e.target.value) || 0)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-2">
        <Input
          placeholder="Start Date"
          type="date"
          value={entry.lease_start_date || ''}
          onChange={(e) => onUpdate(index, 'lease_start_date', e.target.value)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          placeholder="Rent PA"
          value={entry.rent_pa || ''}
          onChange={(e) => onUpdate(index, 'rent_pa', parseFloat(e.target.value) || 0)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-2">
        <Select 
          value={entry.lease_type || 'FRI'} 
          onValueChange={(v) => onUpdate(index, 'lease_type', v)}
          disabled={disabled}
        >
          <SelectTrigger className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="FRI">FRI</SelectItem>
            <SelectItem value="Shared responsibilities">Shared</SelectItem>
            <SelectItem value="Internal only">Internal</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-1 flex items-center justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(index)}
          disabled={disabled}
          className="text-red-500 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Yield Comparable Row Component
const YieldComparableRow = ({ comparable, index, onUpdate, onDelete, disabled }) => {
  return (
    <div className="grid grid-cols-12 gap-2 p-3 bg-white border rounded mb-2" data-testid={`yield-comparable-${index}`}>
      <div className="col-span-2">
        <Input
          placeholder="Property Name"
          value={comparable.property_name || ''}
          onChange={(e) => onUpdate(index, 'property_name', e.target.value)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-2">
        <Input
          placeholder="Address"
          value={comparable.address || ''}
          onChange={(e) => onUpdate(index, 'address', e.target.value)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-1">
        <Input
          placeholder="Use"
          value={comparable.use || ''}
          onChange={(e) => onUpdate(index, 'use', e.target.value)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          placeholder="Sale Price"
          value={comparable.sale_price || ''}
          onChange={(e) => onUpdate(index, 'sale_price', parseFloat(e.target.value) || 0)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-1">
        <Input
          type="date"
          value={comparable.sale_date || ''}
          onChange={(e) => onUpdate(index, 'sale_date', e.target.value)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-1">
        <Input
          type="number"
          step="0.1"
          placeholder="Yield %"
          value={comparable.yield_achieved || ''}
          onChange={(e) => onUpdate(index, 'yield_achieved', parseFloat(e.target.value) || 0)}
          disabled={disabled}
          className="text-sm"
        />
      </div>
      <div className="col-span-2">
        <Select 
          value={comparable.weighting || 'moderate'} 
          onValueChange={(v) => onUpdate(index, 'weighting', v)}
          disabled={disabled}
        >
          <SelectTrigger className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="strong">Strong</SelectItem>
            <SelectItem value="moderate">Moderate</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-1 flex items-center justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(index)}
          disabled={disabled}
          className="text-red-500 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Main Component
export default function InvestmentReportIntelligence({ 
  inspectionId, 
  reportId, 
  reportType,
  token,
  onUpdate
}) {
  const [intelligence, setIntelligence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Auto-save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const lastSavedDataRef = useRef(null);
  const autoSaveIntervalRef = useRef(null);
  const AUTO_SAVE_INTERVAL = 30000; // 30 seconds
  
  // Section open states
  const [openSections, setOpenSections] = useState({
    documents_received: true,
    covenant_strength: false,
    use_section: false,
    state_of_repair: false,
    building_rating: false,
    standard_assumptions: false,
    environmental_assumptions: false,
    gross_yield: false,
    yield_analysis: false
  });

  // Check if this report type needs intelligence sections
  const isInvestmentType = ['investment', 'investment_value', 'commercial_investment', 'healthcare'].includes(reportType);

  // Fetch or create intelligence
  const fetchIntelligence = useCallback(async () => {
    if (!inspectionId || !reportId || !token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Try to get existing
      const getRes = await fetch(
        `${API_URL}/api/report-intelligence/${inspectionId}?report_id=${reportId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const getData = await getRes.json();
      
      if (getData.success && getData.exists) {
        setIntelligence(getData.intelligence);
      } else {
        // Create new
        const createRes = await fetch(
          `${API_URL}/api/report-intelligence`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              inspection_id: inspectionId,
              report_id: reportId,
              report_type: reportType || 'investment'
            })
          }
        );
        
        const createData = await createRes.json();
        if (createData.success) {
          setIntelligence(createData.intelligence);
        } else {
          setError('Failed to create report intelligence');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inspectionId, reportId, reportType, token]);

  useEffect(() => {
    if (isInvestmentType) {
      fetchIntelligence();
    }
  }, [fetchIntelligence, isInvestmentType]);

  // Track changes for auto-save
  useEffect(() => {
    if (!intelligence || !lastSavedDataRef.current) return;
    
    // Compare current intelligence with last saved data
    const currentData = JSON.stringify({
      documents_received: intelligence.documents_received,
      covenant_strength: intelligence.covenant_strength,
      use_section: intelligence.use_section,
      state_of_repair: intelligence.state_of_repair,
      building_rating: intelligence.building_rating,
      standard_assumptions: intelligence.standard_assumptions,
      environmental_assumptions: intelligence.environmental_assumptions,
      gross_yield: intelligence.gross_yield,
      yield_analysis: intelligence.yield_analysis
    });
    
    const savedData = JSON.stringify(lastSavedDataRef.current);
    
    if (currentData !== savedData) {
      setHasUnsavedChanges(true);
    }
  }, [intelligence]);

  // Initialize lastSavedDataRef when intelligence is first loaded
  useEffect(() => {
    if (intelligence && !lastSavedDataRef.current) {
      lastSavedDataRef.current = {
        documents_received: intelligence.documents_received,
        covenant_strength: intelligence.covenant_strength,
        use_section: intelligence.use_section,
        state_of_repair: intelligence.state_of_repair,
        building_rating: intelligence.building_rating,
        standard_assumptions: intelligence.standard_assumptions,
        environmental_assumptions: intelligence.environmental_assumptions,
        gross_yield: intelligence.gross_yield,
        yield_analysis: intelligence.yield_analysis
      };
    }
  }, [intelligence]);

  // Auto-save function
  const performAutoSave = useCallback(async () => {
    if (!intelligence?.id || intelligence.is_locked || !hasUnsavedChanges || saving) {
      return;
    }
    
    setAutoSaveStatus('saving');
    
    try {
      const res = await fetch(
        `${API_URL}/api/report-intelligence/${intelligence.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            documents_received: intelligence.documents_received,
            covenant_strength: intelligence.covenant_strength,
            use_section: intelligence.use_section,
            state_of_repair: intelligence.state_of_repair,
            building_rating: intelligence.building_rating,
            standard_assumptions: intelligence.standard_assumptions,
            environmental_assumptions: intelligence.environmental_assumptions,
            gross_yield: intelligence.gross_yield,
            yield_analysis: intelligence.yield_analysis
          })
        }
      );
      
      const data = await res.json();
      if (data.success) {
        // Update last saved reference
        lastSavedDataRef.current = {
          documents_received: data.intelligence.documents_received,
          covenant_strength: data.intelligence.covenant_strength,
          use_section: data.intelligence.use_section,
          state_of_repair: data.intelligence.state_of_repair,
          building_rating: data.intelligence.building_rating,
          standard_assumptions: data.intelligence.standard_assumptions,
          environmental_assumptions: data.intelligence.environmental_assumptions,
          gross_yield: data.intelligence.gross_yield,
          yield_analysis: data.intelligence.yield_analysis
        };
        
        setHasUnsavedChanges(false);
        setLastAutoSave(new Date());
        setAutoSaveStatus('saved');
        
        // Reset status after 3 seconds
        setTimeout(() => {
          setAutoSaveStatus('idle');
        }, 3000);
        
        if (onUpdate) onUpdate(data.intelligence);
      } else {
        setAutoSaveStatus('error');
        toast.error('Auto-save failed');
      }
    } catch (err) {
      console.error('Auto-save error:', err);
      setAutoSaveStatus('error');
    }
  }, [intelligence, hasUnsavedChanges, saving, token, onUpdate]);

  // Setup auto-save interval
  useEffect(() => {
    if (!intelligence?.id || intelligence.is_locked) {
      return;
    }
    
    // Clear existing interval
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
    }
    
    // Set up new interval
    autoSaveIntervalRef.current = setInterval(() => {
      performAutoSave();
    }, AUTO_SAVE_INTERVAL);
    
    // Cleanup on unmount
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [intelligence?.id, intelligence?.is_locked, performAutoSave]);

  // Save before leaving page (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Update a specific section
  const updateSection = async (sectionId, sectionData) => {
    if (!intelligence?.id || intelligence.is_locked) return;
    
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/report-intelligence/${intelligence.id}/section/${sectionId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(sectionData)
        }
      );
      
      const data = await res.json();
      if (data.success) {
        setIntelligence(data.intelligence);
        if (onUpdate) onUpdate(data.intelligence);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Add lease entry
  const addLeaseEntry = () => {
    if (!intelligence?.documents_received) return;
    
    const newEntries = [
      ...(intelligence.documents_received.lease_entries || []),
      {
        id: Date.now().toString(),
        floor_description: '',
        tenant_name: '',
        tenant_type: 'corporate',
        lease_term_years: 0,
        lease_start_date: '',
        rent_pa: 0,
        lease_type: 'FRI'
      }
    ];
    
    setIntelligence(prev => ({
      ...prev,
      documents_received: {
        ...prev.documents_received,
        lease_entries: newEntries
      }
    }));
  };

  // Update lease entry
  const updateLeaseEntry = (index, field, value) => {
    if (!intelligence?.documents_received?.lease_entries) return;
    
    const newEntries = [...intelligence.documents_received.lease_entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    
    setIntelligence(prev => ({
      ...prev,
      documents_received: {
        ...prev.documents_received,
        lease_entries: newEntries
      }
    }));
  };

  // Delete lease entry
  const deleteLeaseEntry = (index) => {
    if (!intelligence?.documents_received?.lease_entries) return;
    
    const newEntries = intelligence.documents_received.lease_entries.filter((_, i) => i !== index);
    
    setIntelligence(prev => ({
      ...prev,
      documents_received: {
        ...prev.documents_received,
        lease_entries: newEntries
      }
    }));
  };

  // Add yield comparable
  const addYieldComparable = () => {
    if (!intelligence?.yield_analysis) return;
    
    const newComps = [
      ...(intelligence.yield_analysis.yield_comparables || []),
      {
        id: Date.now().toString(),
        property_name: '',
        address: '',
        use: '',
        sale_price: 0,
        sale_date: '',
        yield_achieved: 0,
        weighting: 'moderate'
      }
    ];
    
    setIntelligence(prev => ({
      ...prev,
      yield_analysis: {
        ...prev.yield_analysis,
        yield_comparables: newComps
      }
    }));
  };

  // Update yield comparable
  const updateYieldComparable = (index, field, value) => {
    if (!intelligence?.yield_analysis?.yield_comparables) return;
    
    const newComps = [...intelligence.yield_analysis.yield_comparables];
    newComps[index] = { ...newComps[index], [field]: value };
    
    setIntelligence(prev => ({
      ...prev,
      yield_analysis: {
        ...prev.yield_analysis,
        yield_comparables: newComps
      }
    }));
  };

  // Delete yield comparable
  const deleteYieldComparable = (index) => {
    if (!intelligence?.yield_analysis?.yield_comparables) return;
    
    const newComps = intelligence.yield_analysis.yield_comparables.filter((_, i) => i !== index);
    
    setIntelligence(prev => ({
      ...prev,
      yield_analysis: {
        ...prev.yield_analysis,
        yield_comparables: newComps
      }
    }));
  };

  // Save all changes
  const saveAllSections = async () => {
    if (!intelligence?.id) return;
    
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/report-intelligence/${intelligence.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            documents_received: intelligence.documents_received,
            covenant_strength: intelligence.covenant_strength,
            use_section: intelligence.use_section,
            state_of_repair: intelligence.state_of_repair,
            building_rating: intelligence.building_rating,
            standard_assumptions: intelligence.standard_assumptions,
            environmental_assumptions: intelligence.environmental_assumptions,
            gross_yield: intelligence.gross_yield,
            yield_analysis: intelligence.yield_analysis
          })
        }
      );
      
      const data = await res.json();
      if (data.success) {
        setIntelligence(data.intelligence);
        
        // Update last saved reference
        lastSavedDataRef.current = {
          documents_received: data.intelligence.documents_received,
          covenant_strength: data.intelligence.covenant_strength,
          use_section: data.intelligence.use_section,
          state_of_repair: data.intelligence.state_of_repair,
          building_rating: data.intelligence.building_rating,
          standard_assumptions: data.intelligence.standard_assumptions,
          environmental_assumptions: data.intelligence.environmental_assumptions,
          gross_yield: data.intelligence.gross_yield,
          yield_analysis: data.intelligence.yield_analysis
        };
        
        setHasUnsavedChanges(false);
        setLastAutoSave(new Date());
        toast.success('All changes saved');
        
        if (onUpdate) onUpdate(data.intelligence);
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Calculate WAULT
  const calculateWAULT = async () => {
    if (!intelligence?.id) return;
    
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/report-intelligence/${intelligence.id}/calculate-wault`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await res.json();
      if (data.success) {
        // Update local state with calculated values
        setIntelligence(prev => ({
          ...prev,
          covenant_strength: {
            ...prev.covenant_strength,
            weighted_average_lease_term: data.wault,
            strongest_tenant: data.strongest_tenant,
            strongest_tenant_rent: data.strongest_rent,
            has_break_clauses: data.has_breaks,
            individual_tenant_flag: data.has_individual
          }
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Generate default narratives
  const generateNarratives = async () => {
    if (!intelligence?.id) return;
    
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/report-intelligence/${intelligence.id}/generate-narratives`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await res.json();
      if (data.success) {
        setIntelligence(data.intelligence);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle section
  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // If not an investment type, don't render
  if (!isInvestmentType) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
          <span>Loading report intelligence...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center text-red-600">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>Error: {error}</span>
          </div>
          <Button onClick={fetchIntelligence} className="mt-2">Retry</Button>
        </CardContent>
      </Card>
    );
  }

  if (!intelligence) {
    return null;
  }

  const isLocked = intelligence.is_locked;

  return (
    <Card className="mt-6" data-testid="investment-report-intelligence">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Investment Report Intelligence
          </CardTitle>
          <div className="flex items-center gap-3">
            {/* Auto-save status indicator */}
            {!isLocked && (
              <div className="flex items-center gap-2 text-sm">
                {autoSaveStatus === 'saving' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-blue-50 text-blue-700">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Auto-saving...
                  </Badge>
                )}
                {autoSaveStatus === 'saved' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-green-50 text-green-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Saved
                  </Badge>
                )}
                {autoSaveStatus === 'error' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-red-50 text-red-700">
                    <AlertTriangle className="h-3 w-3" />
                    Save failed
                  </Badge>
                )}
                {autoSaveStatus === 'idle' && hasUnsavedChanges && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-amber-50 text-amber-700">
                    <Clock className="h-3 w-3" />
                    Unsaved changes
                  </Badge>
                )}
                {autoSaveStatus === 'idle' && !hasUnsavedChanges && lastAutoSave && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last saved: {lastAutoSave.toLocaleTimeString()}
                  </span>
                )}
              </div>
            )}
            
            {isLocked ? (
              <Badge variant="destructive" className="flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Locked
              </Badge>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1">
                <Unlock className="h-3 w-3" />
                Editable
              </Badge>
            )}
            <Button 
              onClick={saveAllSections} 
              disabled={isLocked || saving}
              size="sm"
              data-testid="save-intelligence-btn"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save All
            </Button>
          </div>
        </div>
        {/* Auto-save info bar */}
        {!isLocked && (
          <p className="text-xs text-gray-500 mt-2">
            Auto-save enabled (every 30 seconds when changes detected)
          </p>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Section 1: Documents Received */}
        {intelligence.documents_received && (
          <CollapsibleSection
            title="Documents Received"
            icon={FileText}
            status={intelligence.documents_received.status}
            isOpen={openSections.documents_received}
            onToggle={() => toggleSection('documents_received')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Lease Schedule</Label>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addLeaseEntry}
                  disabled={isLocked}
                  data-testid="add-lease-entry-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Lease
                </Button>
              </div>
              
              {/* Header row */}
              <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 px-2">
                <div className="col-span-2">Floor</div>
                <div className="col-span-2">Tenant</div>
                <div className="col-span-1">Term (yrs)</div>
                <div className="col-span-2">Start Date</div>
                <div className="col-span-2">Rent PA (£)</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-1"></div>
              </div>
              
              {intelligence.documents_received.lease_entries?.map((entry, idx) => (
                <LeaseEntryRow
                  key={entry.id || idx}
                  entry={entry}
                  index={idx}
                  onUpdate={updateLeaseEntry}
                  onDelete={deleteLeaseEntry}
                  disabled={isLocked}
                />
              ))}
              
              {intelligence.documents_received.lease_entries?.length === 0 && (
                <p className="text-gray-500 text-center py-4">No lease entries. Click "Add Lease" to add one.</p>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Section 2: Covenant Strength */}
        {intelligence.covenant_strength && (
          <CollapsibleSection
            title="Covenant Strength Summary"
            icon={Shield}
            status={intelligence.covenant_strength.status}
            isOpen={openSections.covenant_strength}
            onToggle={() => toggleSection('covenant_strength')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={calculateWAULT}
                  disabled={isLocked || saving}
                  data-testid="calculate-wault-btn"
                >
                  <Calculator className="h-4 w-4 mr-1" /> Calculate WAULT
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Strongest Tenant</Label>
                  <Input 
                    value={intelligence.covenant_strength.strongest_tenant || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      covenant_strength: {
                        ...prev.covenant_strength,
                        strongest_tenant: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
                <div>
                  <Label>Strongest Rent (£)</Label>
                  <Input 
                    type="number"
                    value={intelligence.covenant_strength.strongest_tenant_rent || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      covenant_strength: {
                        ...prev.covenant_strength,
                        strongest_tenant_rent: parseFloat(e.target.value) || 0
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
                <div>
                  <Label>WAULT (years)</Label>
                  <Input 
                    type="number"
                    step="0.1"
                    value={intelligence.covenant_strength.weighted_average_lease_term || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      covenant_strength: {
                        ...prev.covenant_strength,
                        weighted_average_lease_term: parseFloat(e.target.value) || 0
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={intelligence.covenant_strength.has_break_clauses}
                    onCheckedChange={(checked) => setIntelligence(prev => ({
                      ...prev,
                      covenant_strength: {
                        ...prev.covenant_strength,
                        has_break_clauses: checked
                      }
                    }))}
                    disabled={isLocked}
                  />
                  <Label>Has Break Clauses</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={intelligence.covenant_strength.individual_tenant_flag}
                    onCheckedChange={(checked) => setIntelligence(prev => ({
                      ...prev,
                      covenant_strength: {
                        ...prev.covenant_strength,
                        individual_tenant_flag: checked
                      }
                    }))}
                    disabled={isLocked}
                  />
                  <Label>Has Individual Tenants</Label>
                </div>
              </div>
              
              <div>
                <Label>Covenant Narrative</Label>
                <Textarea 
                  rows={4}
                  value={intelligence.covenant_strength.covenant_narrative || ''}
                  onChange={(e) => setIntelligence(prev => ({
                    ...prev,
                    covenant_strength: {
                      ...prev.covenant_strength,
                      covenant_narrative: e.target.value
                    }
                  }))}
                  disabled={isLocked}
                  placeholder="Enter covenant strength narrative..."
                />
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Section 3: Use */}
        {intelligence.use_section && (
          <CollapsibleSection
            title="Use"
            icon={Building2}
            status={intelligence.use_section.status}
            isOpen={openSections.use_section}
            onToggle={() => toggleSection('use_section')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Primary Use Class</Label>
                  <Input 
                    value={intelligence.use_section.primary_use_class || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      use_section: {
                        ...prev.use_section,
                        primary_use_class: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    placeholder="e.g., Class E"
                  />
                </div>
                <div>
                  <Label>Planning Status</Label>
                  <Input 
                    value={intelligence.use_section.planning_status || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      use_section: {
                        ...prev.use_section,
                        planning_status: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    placeholder="e.g., Lawful use established"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={intelligence.use_section.is_healthcare_property}
                    onCheckedChange={(checked) => setIntelligence(prev => ({
                      ...prev,
                      use_section: {
                        ...prev.use_section,
                        is_healthcare_property: checked
                      }
                    }))}
                    disabled={isLocked}
                  />
                  <Label>Healthcare Property</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={intelligence.use_section.healthcare_comparables_limited}
                    onCheckedChange={(checked) => setIntelligence(prev => ({
                      ...prev,
                      use_section: {
                        ...prev.use_section,
                        healthcare_comparables_limited: checked
                      }
                    }))}
                    disabled={isLocked}
                  />
                  <Label>Limited Healthcare Comparables</Label>
                </div>
              </div>
              
              <div>
                <Label>Use Narrative</Label>
                <Textarea 
                  rows={4}
                  value={intelligence.use_section.use_narrative || ''}
                  onChange={(e) => setIntelligence(prev => ({
                    ...prev,
                    use_section: {
                      ...prev.use_section,
                      use_narrative: e.target.value
                    }
                  }))}
                  disabled={isLocked}
                  placeholder="Enter use/planning narrative..."
                />
              </div>
              
              {intelligence.use_section.healthcare_comparables_limited && (
                <div>
                  <Label>Office Evidence Justification</Label>
                  <Textarea 
                    rows={3}
                    value={intelligence.use_section.office_evidence_justification || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      use_section: {
                        ...prev.use_section,
                        office_evidence_justification: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    placeholder="Justify use of office evidence..."
                  />
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Section 4: State of Repair */}
        {intelligence.state_of_repair && (
          <CollapsibleSection
            title="State of Repair"
            icon={Building2}
            status={intelligence.state_of_repair.status}
            isOpen={openSections.state_of_repair}
            onToggle={() => toggleSection('state_of_repair')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Inspection Extent</Label>
                  <Input 
                    value={intelligence.state_of_repair.inspection_extent || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      state_of_repair: {
                        ...prev.state_of_repair,
                        inspection_extent: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    placeholder="e.g., Internal and external inspection"
                  />
                </div>
                <div>
                  <Label>Overall Presentation</Label>
                  <Select 
                    value={intelligence.state_of_repair.overall_presentation || ''} 
                    onValueChange={(v) => setIntelligence(prev => ({
                      ...prev,
                      state_of_repair: {
                        ...prev.state_of_repair,
                        overall_presentation: v
                      }
                    }))}
                    disabled={isLocked}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Excellent">Excellent</SelectItem>
                      <SelectItem value="Good">Good</SelectItem>
                      <SelectItem value="Fair">Fair</SelectItem>
                      <SelectItem value="Poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Wall Construction</Label>
                  <Input 
                    value={intelligence.state_of_repair.wall_construction || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      state_of_repair: {
                        ...prev.state_of_repair,
                        wall_construction: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
                <div>
                  <Label>Floor Finishes</Label>
                  <Input 
                    value={intelligence.state_of_repair.floor_finishes || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      state_of_repair: {
                        ...prev.state_of_repair,
                        floor_finishes: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
                <div>
                  <Label>Ceiling Finishes</Label>
                  <Input 
                    value={intelligence.state_of_repair.ceiling_finishes || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      state_of_repair: {
                        ...prev.state_of_repair,
                        ceiling_finishes: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lighting</Label>
                  <Input 
                    value={intelligence.state_of_repair.lighting || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      state_of_repair: {
                        ...prev.state_of_repair,
                        lighting: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
                <div>
                  <Label>HVAC</Label>
                  <Input 
                    value={intelligence.state_of_repair.hvac || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      state_of_repair: {
                        ...prev.state_of_repair,
                        hvac: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={intelligence.state_of_repair.is_listed_building}
                    onCheckedChange={(checked) => setIntelligence(prev => ({
                      ...prev,
                      state_of_repair: {
                        ...prev.state_of_repair,
                        is_listed_building: checked
                      }
                    }))}
                    disabled={isLocked}
                  />
                  <Label>Listed Building</Label>
                </div>
                {intelligence.state_of_repair.is_listed_building && (
                  <div className="flex-1">
                    <Select 
                      value={intelligence.state_of_repair.listing_grade || ''} 
                      onValueChange={(v) => setIntelligence(prev => ({
                        ...prev,
                        state_of_repair: {
                          ...prev.state_of_repair,
                          listing_grade: v
                        }
                      }))}
                      disabled={isLocked}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Grade..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Grade I">Grade I</SelectItem>
                        <SelectItem value="Grade II*">Grade II*</SelectItem>
                        <SelectItem value="Grade II">Grade II</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              
              {intelligence.state_of_repair.is_listed_building && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    <AlertTriangle className="h-4 w-4 inline mr-1" />
                    The property's {intelligence.state_of_repair.listing_grade || 'listed'} status may impose limitations on works required to achieve full DDA compliance.
                  </p>
                </div>
              )}
              
              <div>
                <Label>Defects Narrative</Label>
                <Textarea 
                  rows={3}
                  value={intelligence.state_of_repair.defects_narrative || ''}
                  onChange={(e) => setIntelligence(prev => ({
                    ...prev,
                    state_of_repair: {
                      ...prev.state_of_repair,
                      defects_narrative: e.target.value
                    }
                  }))}
                  disabled={isLocked}
                  placeholder="Describe any defects identified..."
                />
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Section 5: Building Rating */}
        {intelligence.building_rating && (
          <CollapsibleSection
            title="Building Rating"
            icon={TrendingUp}
            status={intelligence.building_rating.status}
            isOpen={openSections.building_rating}
            onToggle={() => toggleSection('building_rating')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Star Rating (1-5)</Label>
                  <Select 
                    value={String(intelligence.building_rating.star_rating || '0')} 
                    onValueChange={(v) => setIntelligence(prev => ({
                      ...prev,
                      building_rating: {
                        ...prev.building_rating,
                        star_rating: parseInt(v)
                      }
                    }))}
                    disabled={isLocked}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} Star{n > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rating Type</Label>
                  <Input 
                    value={intelligence.building_rating.rating_type || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      building_rating: {
                        ...prev.building_rating,
                        rating_type: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    placeholder="e.g., healthcare building"
                  />
                </div>
                <div>
                  <Label>Rating Display</Label>
                  <Input 
                    value={intelligence.building_rating.rating_display || `${intelligence.building_rating.star_rating}/5 star ${intelligence.building_rating.rating_type || 'building'}`}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      building_rating: {
                        ...prev.building_rating,
                        rating_display: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={intelligence.building_rating.include_schedule_reference}
                  onCheckedChange={(checked) => setIntelligence(prev => ({
                    ...prev,
                    building_rating: {
                      ...prev.building_rating,
                      include_schedule_reference: checked
                    }
                  }))}
                  disabled={isLocked}
                />
                <Label>Include "See building rating schedule" reference</Label>
              </div>
              
              <div>
                <Label>Rating Narrative</Label>
                <Textarea 
                  rows={3}
                  value={intelligence.building_rating.rating_narrative || ''}
                  onChange={(e) => setIntelligence(prev => ({
                    ...prev,
                    building_rating: {
                      ...prev.building_rating,
                      rating_narrative: e.target.value
                    }
                  }))}
                  disabled={isLocked}
                  placeholder="Additional building rating commentary..."
                />
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Section 6: Standard Assumptions */}
        {intelligence.standard_assumptions && (
          <CollapsibleSection
            title="Standard Assumptions & Conditions"
            icon={FileText}
            status={intelligence.standard_assumptions.status}
            isOpen={openSections.standard_assumptions}
            onToggle={() => toggleSection('standard_assumptions')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-4">
                Standard legal text blocks (locked by default for compliance)
              </p>
              
              {['validity_block', 'responsibility_block', 'publication_block'].map(blockKey => {
                const block = intelligence.standard_assumptions[blockKey];
                if (!block) return null;
                
                return (
                  <div key={blockKey} className="p-3 bg-white border rounded">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-semibold">{block.title}</Label>
                      <Badge variant="outline" className="text-xs">
                        {block.locked_by_default ? 'Standard' : 'Custom'}
                      </Badge>
                    </div>
                    <Textarea 
                      rows={3}
                      value={block.content}
                      onChange={(e) => setIntelligence(prev => ({
                        ...prev,
                        standard_assumptions: {
                          ...prev.standard_assumptions,
                          [blockKey]: {
                            ...prev.standard_assumptions[blockKey],
                            content: e.target.value
                          }
                        }
                      }))}
                      disabled={isLocked || block.locked_by_default}
                      className="text-sm"
                    />
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Section 7: Environmental Assumptions */}
        {intelligence.environmental_assumptions && (
          <CollapsibleSection
            title="Environmental Assumptions"
            icon={Leaf}
            status={intelligence.environmental_assumptions.status}
            isOpen={openSections.environmental_assumptions}
            onToggle={() => toggleSection('environmental_assumptions')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              {['hazardous_materials', 'contaminative_species', 'fire_risk', 'flooding', 'radon'].map(field => (
                <div key={field} className="grid grid-cols-4 gap-4 items-center">
                  <Label className="capitalize">{field.replace(/_/g, ' ')}</Label>
                  <div className="col-span-3">
                    <Input 
                      value={intelligence.environmental_assumptions[field] || ''}
                      onChange={(e) => setIntelligence(prev => ({
                        ...prev,
                        environmental_assumptions: {
                          ...prev.environmental_assumptions,
                          [field]: e.target.value
                        }
                      }))}
                      disabled={isLocked}
                      placeholder="No detectable evidence identified"
                    />
                  </div>
                </div>
              ))}
              
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={intelligence.environmental_assumptions.defects_recorded}
                  onCheckedChange={(checked) => setIntelligence(prev => ({
                    ...prev,
                    environmental_assumptions: {
                      ...prev.environmental_assumptions,
                      defects_recorded: checked
                    }
                  }))}
                  disabled={isLocked}
                />
                <Label>Environmental defects recorded</Label>
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Section 8: Gross Yield */}
        {intelligence.gross_yield && (
          <CollapsibleSection
            title="Valuation Approach - Yield Analysis"
            icon={TrendingUp}
            status={intelligence.gross_yield.status}
            isOpen={openSections.gross_yield}
            onToggle={() => toggleSection('gross_yield')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-4">
                Standard yield theory explanations for investment valuations
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={intelligence.gross_yield.include_gross_yield}
                    onCheckedChange={(checked) => setIntelligence(prev => ({
                      ...prev,
                      gross_yield: {
                        ...prev.gross_yield,
                        include_gross_yield: checked
                      }
                    }))}
                    disabled={isLocked}
                  />
                  <Label>Include Gross Yield Explanation</Label>
                </div>
                {intelligence.gross_yield.include_gross_yield && (
                  <Textarea 
                    rows={2}
                    value={intelligence.gross_yield.gross_yield_explanation}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      gross_yield: {
                        ...prev.gross_yield,
                        gross_yield_explanation: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    className="text-sm"
                  />
                )}
                
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={intelligence.gross_yield.include_net_yield}
                    onCheckedChange={(checked) => setIntelligence(prev => ({
                      ...prev,
                      gross_yield: {
                        ...prev.gross_yield,
                        include_net_yield: checked
                      }
                    }))}
                    disabled={isLocked}
                  />
                  <Label>Include Net Yield Explanation</Label>
                </div>
                {intelligence.gross_yield.include_net_yield && (
                  <Textarea 
                    rows={2}
                    value={intelligence.gross_yield.net_yield_explanation}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      gross_yield: {
                        ...prev.gross_yield,
                        net_yield_explanation: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    className="text-sm"
                  />
                )}
                
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={intelligence.gross_yield.include_ary}
                    onCheckedChange={(checked) => setIntelligence(prev => ({
                      ...prev,
                      gross_yield: {
                        ...prev.gross_yield,
                        include_ary: checked
                      }
                    }))}
                    disabled={isLocked}
                  />
                  <Label>Include ARY Explanation</Label>
                </div>
                {intelligence.gross_yield.include_ary && (
                  <Textarea 
                    rows={2}
                    value={intelligence.gross_yield.ary_explanation}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      gross_yield: {
                        ...prev.gross_yield,
                        ary_explanation: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    className="text-sm"
                  />
                )}
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Section 9: Yield Analysis & Justification */}
        {intelligence.yield_analysis && (
          <CollapsibleSection
            title="Yield Analysis & Justification"
            icon={Calculator}
            status={intelligence.yield_analysis.status}
            isOpen={openSections.yield_analysis}
            onToggle={() => toggleSection('yield_analysis')}
            isLocked={isLocked}
          >
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Yield Comparables</Label>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addYieldComparable}
                  disabled={isLocked}
                  data-testid="add-yield-comparable-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Comparable
                </Button>
              </div>
              
              {/* Header row */}
              <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 px-2">
                <div className="col-span-2">Property</div>
                <div className="col-span-2">Address</div>
                <div className="col-span-1">Use</div>
                <div className="col-span-2">Sale Price</div>
                <div className="col-span-1">Date</div>
                <div className="col-span-1">Yield %</div>
                <div className="col-span-2">Weight</div>
                <div className="col-span-1"></div>
              </div>
              
              {intelligence.yield_analysis.yield_comparables?.map((comp, idx) => (
                <YieldComparableRow
                  key={comp.id || idx}
                  comparable={comp}
                  index={idx}
                  onUpdate={updateYieldComparable}
                  onDelete={deleteYieldComparable}
                  disabled={isLocked}
                />
              ))}
              
              {intelligence.yield_analysis.yield_comparables?.length === 0 && (
                <p className="text-gray-500 text-center py-4">No yield comparables. Click "Add Comparable" to add one.</p>
              )}
              
              <div className="border-t pt-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Adopted Yield (%)</Label>
                    <Input 
                      type="number"
                      step="0.1"
                      value={intelligence.yield_analysis.adopted_yield || ''}
                      onChange={(e) => setIntelligence(prev => ({
                        ...prev,
                        yield_analysis: {
                          ...prev.yield_analysis,
                          adopted_yield: parseFloat(e.target.value) || 0
                        }
                      }))}
                      disabled={isLocked}
                      placeholder="e.g., 5.0"
                    />
                  </div>
                  <div>
                    <Label>Yield Type</Label>
                    <Select 
                      value={intelligence.yield_analysis.yield_type_adopted || 'ARY'} 
                      onValueChange={(v) => setIntelligence(prev => ({
                        ...prev,
                        yield_analysis: {
                          ...prev.yield_analysis,
                          yield_type_adopted: v
                        }
                      }))}
                      disabled={isLocked}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NIY">NIY</SelectItem>
                        <SelectItem value="ARY">ARY</SelectItem>
                        <SelectItem value="Equivalent">Equivalent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      variant="outline" 
                      onClick={generateNarratives}
                      disabled={isLocked || saving}
                      data-testid="generate-narratives-btn"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" /> Generate Justification
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4">
                  <Label>Yield Justification</Label>
                  <Textarea 
                    rows={4}
                    value={intelligence.yield_analysis.adopted_yield_justification || ''}
                    onChange={(e) => setIntelligence(prev => ({
                      ...prev,
                      yield_analysis: {
                        ...prev.yield_analysis,
                        adopted_yield_justification: e.target.value
                      }
                    }))}
                    disabled={isLocked}
                    placeholder="I have hardened the all-risks yield to X% to reflect..."
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>
        )}
      </CardContent>
    </Card>
  );
}
